import React, { useEffect, useState } from 'react';
import { connect, useSelector } from 'react-redux';
import { compose } from 'redux';
import { ReviewCard } from '../../components';
import { PaginationLinks } from '../../../../components';
import { selectActiveFilters } from '../ReviewsFilterDrawer/ReviewsFilterDrawer.duck';

import { useReviews } from '../../../../hooks/useReviews';
import { useUrlFilterSync } from '../../../../hooks/useUrlFilterSync';
import { useScrollRestoration } from '../../../../hooks/useScrollRestoration';
import useRevealOnScroll from '../../../../hooks/useRevealOnScroll';

import css from './ReviewGrid.module.css';

const MOBILE_BREAKPOINT = 767;
const LARGE_DESKTOP_BREAKPOINT = 1281;

const getCardsPerRow = () => {
  if (typeof window === 'undefined' || typeof window.matchMedia !== 'function') {
    return 5;
  }

  if (window.matchMedia(`(max-width: ${MOBILE_BREAKPOINT}px)`).matches) {
    return 2;
  }

  if (window.matchMedia(`(min-width: ${LARGE_DESKTOP_BREAKPOINT}px)`).matches) {
    return 5;
  }

  return 4;
};

const chunkItems = (items, chunkSize) => {
  if (!Array.isArray(items) || items.length === 0) {
    return [];
  }

  const rows = [];

  for (let index = 0; index < items.length; index += chunkSize) {
    rows.push(items.slice(index, index + chunkSize));
  }

  return rows;
};

const ReviewGridItem = ({ listing }) => {
  const { ref, isVisible } = useRevealOnScroll();
  const classes = isVisible ? `${css.revealCard} ${css.revealCardVisible}` : css.revealCard;

  return (
    <div className={classes} ref={ref}>
      <ReviewCard listing={listing} />
    </div>
  );
};

const ReviewGrid = () => {
  const { reviews, loading, error, notFound, pagination, handleGoBack } = useReviews();
  const [cardsPerRow, setCardsPerRow] = useState(getCardsPerRow);

  useUrlFilterSync();
  const filters = useSelector(selectActiveFilters);

  useScrollRestoration(!loading);

  useEffect(() => {
    if (typeof window === 'undefined') {
      return undefined;
    }

    const updateCardsPerRow = () => {
      setCardsPerRow(getCardsPerRow());
    };

    updateCardsPerRow();
    window.addEventListener('resize', updateCardsPerRow);

    return () => window.removeEventListener('resize', updateCardsPerRow);
  }, []);

  const reviewRows = chunkItems(reviews, cardsPerRow);

  return (
    <div className={css.container}>
      {loading && reviews.length === 0 ? (
        <div className={css.productLoading}>Loading reviews...</div>
      ) : null}

      {error ? (
        <div className={css.productError}>Error loading reviews: {error.message}</div>
      ) : null}

      <div className={css.grid}>
        {reviews.length > 0
          ? reviewRows.map((row, rowIndex) => (
              <div
                className={`${css.gridRow} ${css.reviewCardGridRow}`}
                key={`review-row-${rowIndex}`}
              >
                {row.map(listing => (
                  <ReviewGridItem key={listing.id.uuid} listing={listing} />
                ))}
              </div>
            ))
          : !loading &&
            (notFound ? (
              <div className={css.productEmptyState}>
                <div className={css.noReviewsContent}>
                  <p>We couldn't find any reviews matching your filters.</p>
                  <button className={css.noReviewsActions} onClick={handleGoBack}>
                    Go back
                  </button>
                </div>
              </div>
            ) : null)}
      </div>

      {/* Pagination Links */}
      {pagination && pagination.totalPages > 1 ? (
        <PaginationLinks
          className={css.pagination}
          pageName="ReviewsPage"
          pagination={pagination}
          pageSearchParams={filters}
        />
      ) : null}

      {/* Show loading state after clicking load more */}
      {loading && reviews.length > 0 ? (
        <div className={css.productLoading}>Loading more reviews...</div>
      ) : null}
    </div>
  );
};

const mapStateToProps = state => ({});

const mapDispatchToProps = dispatch => ({});

const ReviewGridContainer = compose(
  connect(
    mapStateToProps,
    mapDispatchToProps
  )
)(ReviewGrid);

export default ReviewGridContainer;
